import emptyImage from '../assets/emptyImage.jpg';
import emptyImageDark from '../assets/emptyImage_dark.png';
import albumCreate from '../assets/Album/AlbumCreate.png';
import albumCreateDark from '../assets/Album/AlbumCreate_dark.png';
import albumBase from '../assets/Album/AlbumBase.png';
import albumBaseDark from '../assets/Album/AlbumBase_dark.png';
import logo from '../assets/Logo.png';
import logoDark from '../assets/logo_dark.png';
import kreneBigLogo from '../assets/KreneBigLogo.svg';
import kreneBigLogoDark from '../assets/KreneBigLogo_dark.png';
import leftBg from '../assets/leftBG.svg';
import leftBgDark from '../assets/leftBG_dark.png';
import positionBg from '../assets/positionBG.png';
import positionBgDark from '../assets/positionBG_dark.png';
import invoiceBg from '../assets/invoiceBg.png';
import invoiceBgDark from '../assets/invoiceBg_dark.png';

/**
 * 主题相关图片配置：一个 key 对应亮色 / 暗色两张图。
 *
 * 为什么颜色能用 var(--c-*) 自动翻转、图片不行：
 * 颜色是运行时 CSS 值，CSS 变量一改就跟着变；图片的 URL 在构建期就固化了，
 * 只能在渲染时按当前 resolvedMode 取对应的 src，所以需要这份配置 + useThemeImage。
 *
 * 注意：SVG 被 <img src> 引用时是独立文档，外部的 CSS 变量不会传进去，
 * 所以 SVG 内部必须写死颜色（和 tldraw/SVG 导出路径保持字面量是同一个道理）。
 *
 * 新增一张主题图：
 *   1. 把亮/暗两张图放进 src/assets/
 *   2. 在 THEME_IMAGES 里加一个 key
 *   3. 组件里 const src = useThemeImage('yourKey')
 */

export type ThemeImageKey =
    | 'emptyBoardCover'
    | 'albumCreate'
    | 'albumBase'
    | 'logo'
    | 'kreneBigLogo'
    | 'leftBg'
    | 'positionBg'
    | 'invoiceBg';

export interface ThemeImagePair {
    /** 亮色模式用图，必填 */
    light: string;
    /** 暗色模式用图；缺失时回退到 light（视觉不变，不会渲染成空白） */
    dark?: string;
}

export const THEME_IMAGES: Record<ThemeImageKey, ThemeImagePair> = {
    /** 绘板封面缺省占位图（cover 为空时显示） */
    emptyBoardCover: {
        light: emptyImage,
        dark: emptyImageDark,
    },
    /** 画册页「创建新画册」卡片图标 */
    albumCreate: {
        light: albumCreate,
        dark: albumCreateDark,
    },
    /** 画册卡片的底板模板 */
    albumBase: {
        light: albumBase,
        dark: albumBaseDark,
    },
    /** 品牌 Logo */
    logo: {
        light: logo,
        dark: logoDark,
    },
    /** 首页背景：大 logo 水印 */
    kreneBigLogo: {
        light: kreneBigLogo,
        dark: kreneBigLogoDark,
    },
    /** 首页背景：左侧底纹 */
    leftBg: {
        light: leftBg,
        dark: leftBgDark,
    },
    /** 首页背景：定位底纹 */
    positionBg: {
        light: positionBg,
        dark: positionBgDark,
    },
    /** 开票页背景（.invoice-page / .invoice-panel 的 --invoice-bg） */
    invoiceBg: {
        light: invoiceBg,
        dark: invoiceBgDark,
    },
};

/** 取某个 key 在指定模式下的图片 URL；dark 缺失则回退 light */
export function resolveThemeImage(key: ThemeImageKey, mode: 'light' | 'dark'): string {
    const pair = THEME_IMAGES[key];
    if (!pair) return '';
    if (mode === 'dark') return pair.dark ?? pair.light;
    return pair.light;
}
